import {
    calcSubtotalPerspective,
    calcAccessorialsPerspective,
    getBreakdownPerspective,
    isSelfBilled,
} from '@/components/Accounting/Invoice/utils';
import { PERSPECTIVE_MAP } from '@/components/Accessorials/constants';
import { routeDisplayName } from '@/utilities/routeNames';

const sumAdjustments = (adjustments) =>
    (adjustments || []).reduce((total, { quantity, rate }) => total + (quantity || 0) * (rate || 0), 0);

// Reads in the priority OrderDetailsPricing uses, so a pending edit totals the same here as in the panel.
export const orderPerspectiveTotal = (order, perspective) => {
    const { baseOverride, overrides: adjustmentKey } = PERSPECTIVE_MAP[perspective] || {};
    if (!baseOverride) return 0;

    const adminRate = order?.[baseOverride];
    const base = Number.isFinite(adminRate) ? adminRate : calcSubtotalPerspective(order, perspective) || 0;

    const pendingAdjustments = order?.order_revenue_adjustments?.[adjustmentKey];
    const accessorials =
        pendingAdjustments != null
            ? sumAdjustments(pendingAdjustments)
            : calcAccessorialsPerspective(order, perspective) || 0;

    return base + accessorials;
};

export const orderShipperTotal = (order) => orderPerspectiveTotal(order, 'shipper');

// The leg that actually costs money on a self-billed order is the subcontractor's.
export const carrierPerspective = (order) => (isSelfBilled(order) && order?.contractor_id ? 'contractor' : 'carrier');

export const orderCarrierTotal = (order) => orderPerspectiveTotal(order, carrierPerspective(order));

export const orderMarginDollar = (order) => orderShipperTotal(order) - orderCarrierTotal(order);

export const orderMarginPct = (order) => {
    const shipper = orderShipperTotal(order);
    if (!shipper) return null;
    return (orderMarginDollar(order) / shipper) * 100;
};

export const routeMarginDollar = (orders) => orders.reduce((acc, o) => acc + orderMarginDollar(o), 0);

export const routeMarginPct = (orders) => {
    const rev = orders.reduce((acc, o) => acc + orderShipperTotal(o), 0);
    if (!rev) return null;
    return (routeMarginDollar(orders) / rev) * 100;
};

const PERSPECTIVE_NAMES = {
    shipper: 'Shipper',
    mm_shipper: 'MM Shipper',
    wh_shipper: 'WH Shipper',
    carrier: 'Carrier',
    mm_carrier: 'MM Carrier',
    wh_carrier: 'WH Carrier',
    contractor: 'Contractor',
};

const PERSPECTIVE_LABELS = Object.fromEntries(
    Object.entries(PERSPECTIVE_NAMES).map(([perspective, name]) => [perspective, `${name} Rate`])
);

export const carrierPerspectiveName = (order) => PERSPECTIVE_NAMES[carrierPerspective(order)];

const SHIPPER_PERSPECTIVES = ['shipper', 'mm_shipper', 'wh_shipper'];
const AUDIT_PERSPECTIVE_ORDER = [...SHIPPER_PERSPECTIVES, 'carrier', 'mm_carrier', 'wh_carrier', 'contractor'];

const isLegPriced = (order, perspective) => {
    const { rate, pricing_override_id } = PERSPECTIVE_MAP[perspective] || {};

    return !!(order?.[rate] || order?.[pricing_override_id] || getBreakdownPerspective(order, perspective));
};

export const auditPerspectives = ({ order, canEditPerspective, showAllBreakdowns = false }) => {
    const isVisible = (perspective) =>
        (!canEditPerspective || !!canEditPerspective(order, perspective)) && isLegPriced(order, perspective);

    const slots = showAllBreakdowns ? AUDIT_PERSPECTIVE_ORDER : ['shipper', carrierPerspective(order)];

    return slots.filter(isVisible).map((perspective) => ({
        perspective,
        label: PERSPECTIVE_LABELS[perspective],
        side: SHIPPER_PERSPECTIVES.includes(perspective) ? 'shipper' : 'carrier',
    }));
};

// A carrier auditing its own leg owns the carrier rate but must not see what the shipper was charged,
// or the margin between the two. No callback means the caller opted out of perspective gating.
export const canViewShipperRates = (order, canEditPerspective) =>
    !canEditPerspective || !!canEditPerspective(order, 'shipper');

// One hidden order suppresses the whole total rather than silently under-reporting it.
export const canViewShipperAggregate = (orders, canEditPerspective) =>
    (orders || []).every((order) => canViewShipperRates(order, canEditPerspective));

export const marginHealth = (pct) => {
    if (pct == null) return 'neutral';
    if (pct < 0) return 'bad';
    if (pct < 20) return 'thin';
    return 'good';
};

export const auditTitle = ({ isQueue, route }) => {
    if (isQueue) return 'Audit Orders';
    return route ? `Accessorial Audit · ${routeDisplayName(route)}` : 'Accessorial Audit · Non-Routed Orders';
};
